<script>
    import FormCheckbox from '@/components/shared/form/FormCheckbox';
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';
    import utilsService from '@/services/core/utils.service';

    /**
     * The TinyPumper alerting kill switch. The switch is optimistic: it moves first, then persists.
     * A failed PUT puts it back and says so.
     */
    export default {
        name: 'TinyPumperAlertSwitch',
        components: { FormCheckbox },
        data() {
            return {
                // null, not true — "we have not asked yet" must not render as "alerting is on".
                isAlertingEnabled: null,
                hasOverride: false,
                updatedBy: null,
                updatedAt: null,
                isLoading: false,
                isSaving: false,
                // Only ever set by a failed read. A failed write leaves the state known — it is the
                // value we had before — so it gets a toast and a reverted switch, not an "unknown".
                hasReadFailed: false
            };
        },
        computed: {
            isUnknown() {
                return this.isAlertingEnabled === null;
            },
            isMuted() {
                return this.isAlertingEnabled === false;
            },
            switchLabel() {
                if (this.isUnknown) return 'Alerting: unknown';

                return this.isMuted ? 'Alerts are MUTED — nobody is being told' : 'Alerts on — Slack + daily email';
            },
            switchTitle() {
                return this.isMuted
                    ? 'Turn alerting back on: the next run will post to Slack and the digest will resume.'
                    : 'Mute Slack posts and the daily digest. Incidents keep being detected either way.';
            },
            decidedBy() {
                if (!this.hasOverride || !this.updatedAt) return null;

                const when = new Date(this.updatedAt).toLocaleString();
                return `${this.updatedBy || 'someone'} on ${when}`;
            }
        },
        methods: {
            loadAlerting() {
                this.isLoading = true;
                this.hasReadFailed = false;

                return tinyPumperIncidentsService
                    .alerting()
                    .then(settings => this.applySettings(settings))
                    // Left as unknown rather than defaulting to on. Claiming alerting is live when we
                    // could not read it is the same lie as a muted screen that looks healthy.
                    .catch(() => {
                        this.isAlertingEnabled = null;
                        this.hasReadFailed = true;
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            },
            handleToggle(isEnabled) {
                const previous = this.isAlertingEnabled;

                this.isAlertingEnabled = isEnabled;
                this.isSaving = true;

                return tinyPumperIncidentsService
                    .setAlerting(isEnabled)
                    .then(settings => {
                        this.applySettings(settings);
                        utilsService.showToast(
                            isEnabled
                                ? 'Alerting is back on. The next run will post to Slack.'
                                : 'Alerts muted. Incidents keep being recorded, but nobody will be told.',
                            isEnabled ? 'success' : 'warn'
                        );
                    })
                    // Reverted, not left where the operator dragged it: believing alerting is muted
                    // when it is not sends the flood anyway, and believing it is live when it is not
                    // is the silence this whole screen exists to make visible.
                    .catch(() => {
                        this.isAlertingEnabled = previous;
                        utilsService.showToast('Could not change alerting. The switch was put back.', 'warn');
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            },
            applySettings(settings) {
                this.isAlertingEnabled = settings?.isAlertingEnabled ?? null;
                this.hasOverride = !!settings?.hasOverride;
                this.updatedBy = settings?.updatedBy ?? null;
                this.updatedAt = settings?.updatedAt ?? null;
            }
        },
        mounted() {
            this.loadAlerting();
        }
    };
</script>

<template>
    <div class="tp-alert-switch" :class="{ 'tp-alert-switch--muted': isMuted }">
        <FormCheckbox
            switch
            id="tp-health-alerting"
            :modelValue="isAlertingEnabled === true"
            :disabled="isLoading || isSaving || isUnknown"
            :labelClass="isMuted ? 'fw-bold text-danger text-nowrap' : 'text-nowrap'"
            :title="switchTitle"
            @change="isEnabled => handleToggle(isEnabled)"
        >
            <i class="bi me-1" :class="isMuted ? 'bi-bell-slash-fill' : 'bi-bell-fill'"></i>
            {{ switchLabel }}
        </FormCheckbox>

        <div v-if="isMuted" class="alert alert-danger tp-alert-switch__warning mb-0" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            <b>TinyPumper alerts are muted.</b>
            The engine is still running, so the incidents on this page are current — but no Slack message and no daily
            digest is going out, and this screen is the only place anyone will see them. A quiet channel right now does
            <b>not</b> mean the fleet is healthy; it means nobody has been told.
            <span v-if="decidedBy">Muted by {{ decidedBy }}.</span>
            Switch alerting back on as soon as you are done here.
        </div>

        <div v-else-if="hasReadFailed" class="alert alert-warning tp-alert-switch__warning mb-0" role="alert">
            <i class="bi bi-question-circle-fill me-2"></i>
            We could not read whether alerting is on. Treat this page as the only thing watching the fleet until it
            loads.
        </div>
    </div>
</template>

<style lang="scss">
    .tp-alert-switch {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        &__warning {
            font-size: 0.875rem;
            line-height: 1.5;
            padding: 0.5rem 0.75rem;
            max-width: 46rem;
        }

        // The muted state has to be impossible to skim past: a strip of alarm colour behind the
        // control itself, not just a sentence underneath it that a hurried operator scrolls off.
        &--muted {
            border-left: 4px solid var(--bs-danger);
            padding-left: 0.75rem;
            background: rgba(var(--bs-danger-rgb), 0.06);
            border-radius: 0.25rem;

            .form-check-input:not(:checked) {
                border-color: var(--bs-danger);
            }
        }
    }
</style>
